import get from 'lodash/get';

import { isPlainObject } from '../../../utils';
import type { ComputePaths } from '../types';

/** 将嵌套对象展平为 Arco Form 的点路径 patch（如 resource.gpu.number） */
export function flattenToFormPatch(basePath: string, value: unknown, patch: Record<string, unknown> = {}): Record<string, unknown> {
  if (value === undefined || value === null) {
    if (basePath) patch[basePath] = undefined;
    return patch;
  }
  if (!isPlainObject(value) || Array.isArray(value)) {
    if (basePath) patch[basePath] = value;
    return patch;
  }
  const entries = Object.entries(value);
  if (!entries.length) {
    if (basePath) patch[basePath] = value;
    return patch;
  }
  entries.forEach(([key, child]) => {
    const nextPath = basePath ? `${basePath}.${key}` : key;
    flattenToFormPatch(nextPath, child, patch);
  });
  return patch;
}

export function clearNestedFormFields(form: import('@arco-design/web-react').FormInstance, basePath: string, value: unknown) {
  const patch = flattenToFormPatch(basePath, value);
  const cleared = Object.fromEntries(Object.keys(patch).map((key) => [key, undefined]));
  if (Object.keys(cleared).length) {
    form.setFieldsValue(cleared);
  }
  form.setFieldValue(basePath, undefined);
}

/** 抽屉 hydrate：把 resource 父对象拆成已挂载叶子，避免卡片摘要读到空值 */
export function hydrateComputeResourceLeaves(
  form: import('@arco-design/web-react').FormInstance,
  resource: Record<string, unknown> | undefined,
  paths: Pick<ComputePaths, 'cpuRequest' | 'memory' | 'gpu'>,
) {
  if (!resource) return;
  const patch: Record<string, unknown> = {};
  const cpuRequest = get(resource, 'cpu.request');
  if (paths.cpuRequest && cpuRequest != null && typeof cpuRequest !== 'object') {
    patch[paths.cpuRequest] = cpuRequest;
  }
  const memory =
    get(resource, 'memory.request') ??
    (typeof resource.memory === 'number' || typeof resource.memory === 'string' ? resource.memory : undefined);
  if (memory != null && typeof memory !== 'object') {
    patch[paths.memory] = memory;
  }
  if (resource.gpu && typeof resource.gpu === 'object') {
    Object.assign(patch, flattenToFormPatch(paths.gpu, resource.gpu));
  }
  if (resource.cpuPricing && typeof resource.cpuPricing === 'object') {
    Object.assign(patch, flattenToFormPatch('resource.cpuPricing', resource.cpuPricing));
  }
  if (resource.memoryPricing && typeof resource.memoryPricing === 'object') {
    Object.assign(patch, flattenToFormPatch('resource.memoryPricing', resource.memoryPricing));
  }
  if (Object.keys(patch).length) {
    form.setFieldsValue(patch);
  }
}
